import * as React from 'react';
import { ReactNode } from 'react';
import { TypographyProps } from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon/SvgIcon';
import type { MutationHookOptions } from '@apollo/client/react/types/types';
import type { OperationVariables } from '@apollo/client/core';

// Base

export interface BaseModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
}

// Common

export interface SelectOption<T extends string | number | boolean = string> {
  label: string;
  value: T;
}

export interface TabsOption<TabsValue extends string = string> {
  label: string;
  value: TabsValue;
  disabled?: boolean;
  isVisible?: boolean;
  component: ReactNode;
  urlAnchor?: `#${TabsValue}`;
  iconStart?: React.ReactElement<unknown>;
  iconEnd?: React.ReactElement<unknown>;
}

export interface ButtonGroupOption<ButtonGroupValue extends string = string> extends TabsOption<ButtonGroupValue> {}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
}

// Enums

export enum PageTypes {
  VendorOpportunities = 'vo',
  CreditSubmission = 'cs',
}

export enum ContentViewType {
  Desktop = 'Desktop',
  Mobile = 'Mobile',
}

// Utils

export type Nullable<T> = null | T;

export type ValueOf<T, K extends keyof T> = T[K];

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

type OnlyPrimitiveObjectFields<
  Object extends Record<string, any>,
  Restriction extends string | number | boolean,
> = Pick<Object, { [Key in keyof Object]: Object[Key] extends Restriction ? Key : never }[keyof Object]>;

type OnlyPrimitiveObjectKeys<Object extends Record<string, any>, Restriction extends string | number | boolean> = {
  [Key in keyof Object]: Object[Key] extends Restriction ? Key : never;
}[keyof Object];

export enum PrescreenTypes {
  Prescreen = 'Prescreen',
  LenderWaterfall = 'LenderWaterfall',
  Demo = 'Demo',
  Off = 'Off',
}

export enum ModalsKeys {
  Load = 'load',
  CreateEquipment = 'createEquipment',
  Confirmation = 'confirmation',
  FinanceForm = 'financeFormDialog',
  PreviewFinanceForm = 'previewFinanceForm',
  ProposalSentDialog = 'proposalSentDialog',
  ProposalProductsDialog = 'proposalProductsDialog',
  ProposalMenuOptionDetailsDialog = 'proposalMenuOptionDetailsDialog',
  ProposalContractDetailsDialog = 'proposalContractDetailsDialog',
  ProposalCreationDialog = 'proposalCreationDialog',
  ProposalCreateProductDialog = 'proposalCreateProductDialog',

  DeskingConfirmSaveDraftDialog = 'confirmSaveDraftDialog',
  DeskingNotifyAboutRemovedProductsDialog = 'deskingNotifyAboutRemovedProductsDialog',

  ProductCatalogDialog = 'productCatalogDialog',

  ConstructorPricingInfoDialog = 'constructorPricingInfoDialog',

  AftermarketAddEditMenuOptionDialog = 'addEditMenuOptionDialog',
  AftermarketSaveAsDialog = 'aftermarketSaveAsDialog',
  AftermarketShareMenuConfirmationDialog = 'aftermarketShareMenuConfirmationDialog',

  AftermarketPartnerAddDealerDialog = 'aftermarketPartnerAddDealerDialog',
  AftermarketPartnerAddLocationDialog = 'aftermarketPartnerAddLocationDialog',
  AftermarketCreatePartnerDialog = 'aftermarketCreatePartnerDialog',

  PrescreenProgramsDialog = 'programsDialog',

  CreateUpdateInvoiceDialog = 'createUpdateInvoiceDialog',

  CorporateGuarantorDialog = 'corporateGuarantorDialog',
  CreateUpdateEquipmentDialog = 'createUpdateEquipmentDialog',
  ChargesEquipmentDialog = 'chargesEquipmentDialog',
  CreateChargesEquipmentDialog = 'createChargesEquipmentDialog',
  PreviewExistingEquipmentImagesDialog = 'previewExistingEquipmentImagesDialog',
  PreviewNewEquipmentImagesDialog = 'previewNewEquipmentImagesDialog',
  UploadImagesForExistingEquipmentDialog = 'uploadImagesForExistingEquipmentDialog',
  UploadImagesForNewEquipmentDialog = 'uploadImagesForNewEquipmentDialog',

  InsuranceSelectSubmissionDialog = 'insuranceSelectSubmissionDialog',
  InsuranceAddEditBrokerDialog = 'insuranceAddEditBrokerDialog',
  InsuranceEnterManualLenderRequirementsDialog = 'insuranceEnterManualLenderRequirementsDialog',

  SendInsuranceQuoteDialog = 'sendInsuranceQuoteDialog',

  AddEditDeclineReasonDialog = 'addEditDeclineReasonDialog',

  CreditSubmissionDetailsDialog = 'creditSubmissionDetailsDialog',

  SyncDealInfoDialog = 'syncDealInfoDialog',

  IntegrationSettingsDialog = 'integrationSettingsDialog',

  DocuSignDialog = 'docuSignDialog',

  QuoteProgramDialog = 'quoteProgramDialog',

  AddContingencyDialog = 'addContingencyDialog',

  ActivityDialog = 'activityDialog',
  CallActivityDialog = 'callActivityDialog',
  NoteActivityDialog = 'noteActivityDialog',

  ViewBroadcastDialog = 'viewBroadcastDialog',

  ExternalDashboardDialog = 'externalDashboardDialog',

  SearchInventoryDialog = 'searchInventoryDialog',
}

// Redux

type InferValueTypes<T> = T extends { [key: string]: infer U } ? U : never;
export type ActionTypes<T extends { [key: string]: (...args: any) => any }> = ReturnType<InferValueTypes<T>>;

// Components

export type Variants = 'info' | 'warning' | 'error' | 'notification';

export interface FieldNotify {
  hide?: boolean;
  message: string;
  variant?: Variants;
  withIcon?: boolean;
  iconTooltip?: string;
  iconProps?: SvgIconProps;
  typographyProps?: TypographyProps;
}

// API

export interface PaginationResponse<ResponseData = any> {
  page: number;
  total: number;
  perPage: number;
  totalPages: number;
  data: Array<ResponseData>;
}

export interface APIRequestPagination {
  page: number;
  perPage: number;
}

export interface APIRequestSort<Key = string> {
  key: Key;
  value: 'asc' | 'desc';
}

export interface MutationResponse<Entity> {
  result: {
    success: boolean;
    entity: Entity;
    error: Nullable<string>;
  };
}

export interface MutationListResponse<Entity> {
  result: {
    success: boolean;
    list: Entity[];
    error: Nullable<string>;
  };
}

export interface QueryResponse<Entity> {
  result: {
    entity: Entity;
    success: boolean;
    error: Nullable<string>;
  };
}

export interface QueryListResponse<Entity> {
  result: {
    list: Entity[];
    success: boolean;
    error: Nullable<string>;
  };
}

export type GraphqlResolverInput<Input> = { input: Input };

export type FetchOptionsCallback<API, TData = any, TVars = OperationVariables> = (
  api: API
) => Omit<MutationHookOptions<TData, TVars>, 'variables'>;
